import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useEffect } from "react";

import { TicketingFormCategory } from "@simplyk/common";
import { RegisterOptions, useFormContext, useFormState, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { isMin } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const MinPerOrderField: FC = () => {
  const { t } = useTranslate();
  const { rateIndex, formCategory, isRegistration, isSweepstakes } = useRateContext();
  const { control, trigger } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("rateMinimumNumberToBuy");
  const maxFieldName = useRateFieldName("rateMaximumNumberToBuy");
  const seatsFieldName = useRateFieldName("seats");
  const error = useRateFieldError("rateMinimumNumberToBuy");
  const { isSubmitted, dirtyFields } = useFormState({ control, name: fieldName });

  const maxValue = useWatch({ control, name: maxFieldName });
  const seatsValue = useWatch({ control, name: seatsFieldName });

  useEffect(() => {
    if (isSubmitted || dirtyFields?.rates?.[rateIndex]?.rateMinimumNumberToBuy) {
      void trigger(fieldName);
    }
  }, [maxValue, seatsValue, trigger, fieldName, isSubmitted, dirtyFields, rateIndex]);

  const isShop = formCategory === TicketingFormCategory.Shop;
  const tooltipKey = isShop
    ? "ticketing.rateMinimumNumberToBuyInformationsShop"
    : isRegistration
      ? "ticketing.rateMinimumNumberToBuyInformationsRegistration"
      : "ticketing.rateMinimumNumberToBuyInformations";
  const tooltip = isSweepstakes
    ? t("dashboard", "sweepstakes.rates.minPerParticipationTooltip")
    : t("dashboard", tooltipKey);

  const notExceedingCapMessage = t(
    "dashboard",
    isSweepstakes ? "sweepstakes.rates.minExceedsCap" : "ticketing.rateMinimumNumberToBuyExceedsCap"
  );
  const notExceedingCapRule: RegisterOptions = {
    validate: {
      notExceedingCap: (value: string | number | null | undefined) => {
        const min = toFiniteNumber(value);
        if (min === null) {
          return true;
        }
        const max = toFiniteNumber(maxValue);
        const seats = toFiniteNumber(seatsValue);
        const cap = Math.min(max ?? Infinity, seats ?? Infinity);
        if (Number.isFinite(cap) && min > cap) {
          return notExceedingCapMessage;
        }
        return true;
      },
    },
  };

  return (
    <DashboardTextField
      label={
        <span>
          <DashboardTypography variant="body2" sx={{ display: "inline", mr: 0.5 }}>
            {t(
              "dashboard",
              isSweepstakes ? "sweepstakes.rates.minPerParticipation" : "ticketing.rateMinimumNumberToBuy"
            )}
          </DashboardTypography>
          <DashboardInformationTooltip title={tooltip} placement="top" alignOnText />
        </span>
      }
      name={fieldName}
      placeholder={t("dashboard", "ticketing.none")}
      control={control}
      size="medium"
      errorMessage={error?.message}
      rules={[isMin(0, t("dashboard", "ticketing.rateMinimumNumberToBuyMinimum")), notExceedingCapRule]}
      type="number"
      defaultValue={1}
    />
  );
};

const toFiniteNumber = (raw: unknown): number | null => {
  if (raw === undefined || raw === null || raw === "") {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
